import JsonURL from "@jsonurl/jsonurl";
import {
  AgentPrintActionRequest,
  BookingPackageBookRequest,
  BookingPackageDossier,
  BookingPackageRequest,
  book,
  priceDetails,
  print,
} from "@qite/tide-client";
import { format, formatISO, isSameDay, parse } from "date-fns";
import { cloneDeep, first, isEmpty, last, omit } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useMemberStore } from "../app/member-store/context";
import { useProductStore } from "../app/search-results/product-store/context";
import {
  setEstimatedPrice,
  setFlightsFlyInIsOpen,
  setHasFlight,
  setHasTransfer,
  setIsUnavailable,
  setPrice,
  setRegimeFlyInIsOpen,
} from "../app/search-results/product-store/reducer";
import { useQSMStore } from "../app/search-results/qsm-store/context";
import {
  setBookingPackage,
  setBookingRooms,
  setDateRange,
  setFlight,
  setIsLoading,
  setRooms,
} from "../app/search-results/qsm-store/reducer";
import DatePicker from "../app/search-results/qsm/date-picker";
import { Room } from "../app/search-results/qsm/room-picker";
import { sortProps } from "../app/search-results/qsm/utils";
import { getPackageDetails } from "../app/search-results/utils/search-util";
import { buildTideClientConfig, officeId } from "../tide-api";
import {
  formatter,
  handleAnchorClick,
  selectAgentToken,
  selectCorrectRoomInBookingPackage,
  selectCorrectRoomInQsmRooms,
} from "../utils";
import Icon from "./icon";
import Loader from "./loader";
import Rating from "./rating";
import ProductIntroRooms from "./product-intro/product-intro-rooms";
import { useI18next } from "gatsby-plugin-react-i18next";
import LabeledCheckbox from "./labeled-checkbox";
import { getTideLanguageId } from "../i18n/languages";
import Link from "./link";

interface ProductIntroProps {
  title: string;
  introductionTitle?: string;
  introduction: string;
  factsheetUrl?: string;
  destination: string;
  rating?: number;
  mediaComponent?: React.ReactElement;
  qsmComponent?: React.ReactElement;
  pricingComponent?: React.ReactElement;
}

interface PrintButtonState {
  isLoading: boolean;
  label: string;
}

const ProductIntro: React.FC<ProductIntroProps> = ({
  title,
  introductionTitle,
  introduction,
  factsheetUrl,
  destination,
  rating,
  mediaComponent,
  pricingComponent,
}) => {
  const { t, language } = useI18next();

  const [qsmState, qsmDispatch] = useQSMStore();
  const [productState, productDispatch] = useProductStore();
  const [memberState] = useMemberStore();

  const [flyInHasBeenOpened, setFlyInHasBeenOpened] = useState<boolean>(false);
  const [isBookable, setBookable] = useState<boolean>();
  const [requestRooms, setRequestRooms] = useState<Room[]>(qsmState?.rooms ?? []);
  const [datePanelActive, setDatePanelActive] = useState<boolean>(false);

  const [offer, setOffer] = useState<BookingPackageDossier>();
  const [printButtonState, setPrintButtonState] = useState<PrintButtonState>({
    isLoading: false,
    label: t("PRINT_OFFER"),
  });
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [isFetchingPrice, setIsFetchingPrice] = useState<boolean>(true);

  const [showFactsheet, setShowFactsheet] = useState<boolean>(false);

  const fetchPackage = async (signal?: AbortSignal) => {
    if (
      qsmDispatch &&
      qsmState?.product &&
      qsmState?.dateRange?.fromDate &&
      qsmState?.dateRange?.toDate &&
      qsmState?.rooms
    ) {
      try {
        // Fetch package
        qsmDispatch(setIsLoading(true));
        if (productDispatch) {
          productDispatch(setIsUnavailable(false));
          productDispatch(setPrice(undefined));
          productDispatch(setEstimatedPrice(undefined));
        }

        const response = await getPackageDetails(
          qsmState?.rooms,
          formatISO(qsmState?.dateRange?.fromDate, { representation: "date" }) + "T00:00:00Z",
          formatISO(qsmState?.dateRange?.toDate, { representation: "date" }) + "T00:00:00Z",
          qsmState?.product?.content?.general?.product?.code,
          signal,
          language
        );

        if (!response.errorCode) {
          const bookingPackage = selectCorrectRoomInBookingPackage(
            qsmState?.rooms,
            cloneDeep(response.payload)
          );
          if (bookingPackage) {
            // This is done to catch the cases were the previous selected rooms in unavailable in the new package
            const qsmRooms = cloneDeep(qsmState?.rooms);
            const roomsChanged = selectCorrectRoomInQsmRooms(qsmRooms, bookingPackage);
            if (roomsChanged) {
              qsmDispatch(setRooms(qsmRooms));
            }
          }
          qsmDispatch(setBookingPackage(bookingPackage));
        } else {
          qsmDispatch(setBookingPackage(undefined));
        }
        if (!qsmState?.bookingPackage && productDispatch) {
          productDispatch(setIsUnavailable(true));
        }
        qsmDispatch(setIsLoading(false));
      } catch (ex: any) {
        if (productDispatch) {
        }

        // Something went wrong
        setBookable(false);
        if (!ex?.toString().includes("user aborted")) {
          qsmDispatch(setIsLoading(false));
        }
      }
    }
  };

  const handleBookClick: React.MouseEventHandler<HTMLButtonElement> = () => {
    const params: Record<string, string> = {};

    if (tagIds?.length) {
      params["tagIds"] = tagIds.join(",");
    }

    if (qsmState?.rooms) {
      const serialized = JsonURL.stringify(
        qsmState.rooms.map((room) => omit(room, ["children"])),
        { AQF: true }
      );

      if (serialized) {
        params["rooms"] = serialized;
      }
    }

    if (qsmState?.dateRange?.fromDate) {
      params["startDate"] = format(qsmState.dateRange.fromDate, "yyyy-MM-dd");
    }

    if (qsmState?.dateRange?.toDate) {
      params["endDate"] = format(qsmState.dateRange.toDate, "yyyy-MM-dd");
    }

    if (qsmState?.flight?.outward && qsmState.flight.return) {
      var flight = qsmState.flight;

      const flightInfo = {
        outwardCode: flight.outward.code,
        outwardNumbers: flight.outward.flightMetaData.flightLines.map((x) => x.number),
        outwardClass: flight.outward.flightMetaData.flightLines[0].flightClass,
        returnCode: flight.return.code,
        returnNumbers: flight.return.flightMetaData.flightLines.map((x) => x.number),
        returnClass: flight.return.flightMetaData.flightLines[0].flightClass,
      };

      const serialized = JsonURL.stringify(flightInfo, { AQF: true });

      if (serialized) {
        params["flight"] = serialized;
      }
    }

    params["catalog"] = "1";

    const path = window.location.pathname;

    const paramString = Object.keys(params)
      .map((key) => `${key}=${params[key]}`)
      .join("&");

    window.location.href = `${path}${path.endsWith("/") ? "" : "/"}boeken?${paramString}`;
  };

  const handleRegimeClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (typeof window !== "undefined" && typeof document !== "undefined") {
      event.preventDefault();
      if (productDispatch && !qsmState?.isLoading) {
        productDispatch(setRegimeFlyInIsOpen(true));
        setFlyInHasBeenOpened(true);
      }
    }
  };

  const handleFlightsClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (typeof window !== "undefined" && typeof document !== "undefined") {
      event.preventDefault();
      if (productDispatch) productDispatch(setFlightsFlyInIsOpen(true));
    }
  };

  const getPax = () => {
    const selectedOption =
      qsmState?.bookingPackage?.options.find((x) => x.isSelected) ??
      qsmState?.bookingPackage?.options[0];
    return selectedOption?.requestRooms.flatMap((room) => room.pax);
  };

  const createOffer = async (): Promise<BookingPackageDossier> => {
    const pax = getPax();

    if (!qsmState?.bookingPackage || !qsmState || !memberState?.member || !pax) {
      throw new Error("Invalid input");
    }

    const { member } = memberState;
    const tideClientConfig = buildTideClientConfig(selectAgentToken());

    let newOffer: BookingPackageDossier;
    // if (offer) {
    //   const request: BookingPackageRequest<BookingPackageUpdateRequest> = {
    //     officeId,
    //     agentId: member.agentId,
    //     payload: {
    //       dossierNumber: offer.number,
    //       package: qsmState?.bookingPackage,
    //       status: 0,
    //       pax,
    //       nonTravelPax: [],
    //       notifications: [],
    //       tagIds: tagIds,
    //       customerRequests: [],
    //     },
    //   };

    //   newOffer = await update(config, request);
    // } else {
    const request: BookingPackageRequest<BookingPackageBookRequest> = {
      officeId,
      agentId: member.agentId,
      payload: {
        package: qsmState?.bookingPackage,
        status: 0,
        pax,
        nonTravelPax: [],
        notifications: [],
        tagIds: tagIds,
        customerRequests: [],
      },
    };

    newOffer = await book(tideClientConfig, request, undefined, language);
    // }

    setOffer(newOffer);
    return newOffer;
  };

  const handleTagClick: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const tagId = Number(e.target.value);
    if (e.target.checked) {
      var updatedTagIds = tagIds.concat([tagId]);
      setTagIds(updatedTagIds);
    } else {
      var updatedTagIds = tagIds.filter((x) => x != tagId);
      setTagIds(updatedTagIds);
    }
  };

  const handlePrintOfferClick = async () => {
    // Build offer
    setPrintButtonState({ isLoading: true, label: t("GENERATE_OFFER") });
    const _offer = await createOffer();

    // Print offer
    if (!_offer) throw new Error("An offer is required");
    setPrintButtonState({ isLoading: true, label: t("GENERATE_PRINT") });
    await printOffer(_offer);

    // Done
    setPrintButtonState({ isLoading: false, label: t("PRINT_OFFER") });
  };

  const printOffer = async (_offer: BookingPackageDossier) => {
    if (!_offer) throw new Error("An offer is required");

    const tideClientConfig = buildTideClientConfig(selectAgentToken());
    const printActionId = parseInt(process.env.OFFER_PRINT_ID!); // 'Mooie offerte (website)'
    const tideLanguageId = getTideLanguageId(language);
    const request: AgentPrintActionRequest = {
      id: printActionId,
      dossierNumber: _offer.number,
      languageId: tideLanguageId,
    };

    const response = await print(tideClientConfig, request).catch();
    const arrayBuffer = await response.arrayBuffer();
    const blob = new Blob([arrayBuffer], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    window.open(url);
  };

  const updateHistory = (isFirstTime: boolean) => {
    const params: Record<string, string> = {};
    const incomingParams = new URLSearchParams(location.search);
    const incomingAcco = incomingParams.get("accommodation");
    const incomingRegime = incomingParams.get("regime");

    if (incomingAcco && isFirstTime) {
      params["accommodation"] = incomingAcco;
    }

    if (incomingRegime && isFirstTime) {
      params["regime"] = incomingRegime;
    }

    if (qsmState?.rooms) {
      const serialized = JsonURL.stringify(
        qsmState?.rooms.map((room) => {
          if (incomingAcco && isFirstTime) {
            room.accommodationCode = incomingAcco;
          }
          if (incomingRegime && isFirstTime) {
            room.regimeCode = incomingRegime;
          }
          return omit(room, ["children"]);
        }),
        { AQF: true }
      );

      if (serialized) {
        params["rooms"] = serialized;
      }
    }

    if (qsmState?.dateRange.fromDate) {
      params["from"] = format(qsmState?.dateRange.fromDate, "yyyyMMdd");
    }

    if (qsmState?.dateRange.toDate) {
      params["to"] = format(qsmState?.dateRange.toDate, "yyyyMMdd");
    }

    if (qsmState?.flight?.outward && qsmState.flight.return) {
      var flight = qsmState.flight;

      const flightInfo = {
        outwardCode: flight.outward.code,
        outwardNumbers: flight.outward.flightMetaData.flightLines.map((x) => x.number),
        outwardClass: flight.outward.flightMetaData.flightLines[0].flightClass,
        returnCode: flight.return.code,
        returnNumbers: flight.return.flightMetaData.flightLines.map((x) => x.number),
        returnClass: flight.return.flightMetaData.flightLines[0].flightClass,
      };

      const serialized = JsonURL.stringify(flightInfo, { AQF: true });

      if (serialized) {
        params["flight"] = serialized;
      }
    }

    const path = window.location.pathname;

    const paramString = Object.keys(params)
      .map((key) => `${key}=${params[key]}`)
      .join("&");

    window.history.replaceState("", "", `${path}?${paramString}`);
  };

  //   const calculatePerBookingPrice = (group: BookingOptionGroup<PerBookingPackageOption>) => {
  //     let bookingTotal = 0;
  //     group.options.forEach((option) => {
  //       if (option.isSelected) {
  //         bookingTotal += option.line.price;

  //         bookingTotal += option.groups.reduce((total, g) => total + calculatePerBookingPrice(g), 0);

  //         option.units.forEach((unit) => {
  //           bookingTotal += unit.groups.reduce((total, g) => total + calculatePerUnitPrice(g), 0);
  //         });

  //         option.pax.forEach((pax) => {
  //           bookingTotal += pax.groups.reduce((total, g) => total + calculatePerPaxPrice(g), 0);
  //         });
  //       }
  //     });

  //     return bookingTotal;
  //   };

  //   const calculatePerUnitPrice = (group: BookingOptionGroup<PerUnitPackageOption>) => {
  //     let unitTotal = 0;

  //     group.options.forEach((option) => {
  //       if (option.isSelected) {
  //         unitTotal += option.line.price;

  //         unitTotal += option.groups.reduce((total, g) => total + calculatePerUnitPrice(g), 0);

  //         option.pax.forEach((pax) => {
  //           unitTotal += pax.groups.reduce((total, g) => total + calculatePerPaxPrice(g), 0);
  //         });
  //       }
  //     });

  //     return unitTotal;
  //   };

  //   const calculatePerPaxPrice = (group: BookingOptionGroup<PerPaxPackageOption>) => {
  //     let paxTotal = 0;

  //     group.options.forEach((option) => {
  //       if (option.isSelected) {
  //         paxTotal += option.line.price;

  //         paxTotal += option.groups.reduce((total, g) => total + calculatePerPaxPrice(g), 0);
  //       }
  //     });

  //     return paxTotal;
  //   };

  const updatePrice = async () => {
    const pax = getPax();

    if (!memberState || !qsmState?.bookingPackage || !productDispatch || !pax) return;

    setIsFetchingPrice(true);
    const tideClientConfig = buildTideClientConfig(selectAgentToken());
    const { member } = memberState;

    const request: BookingPackageRequest<BookingPackageBookRequest> = {
      officeId: officeId,
      agentId: member?.agentId,
      payload: {
        package: qsmState.bookingPackage,
        status: 0,
        tagIds: tagIds,
        pax: pax,
        nonTravelPax: [],
        notifications: [],
        customerRequests: [],
      },
    };

    if (qsmState?.bookingPackage) {
      const result = await priceDetails(tideClientConfig, request).finally(() =>
        setIsFetchingPrice(false)
      );
      // Todo: Update booking with changed lines? (result.changedLines)
      const totalPrice = result?.details?.reduce((prev, curr) => prev + curr.total, 0);
      productDispatch(setPrice({ defaultPrice: totalPrice }));
    }
  };

  useEffect(() => {
    if (!productDispatch || !qsmDispatch) return;

    const bookingPackage = qsmState?.bookingPackage;
    const selectedOption = bookingPackage?.options.find((x) => x.isSelected);

    if (bookingPackage && selectedOption) {
      const selectedOutwardFlight = bookingPackage.outwardFlights.find((x) => x.isSelected);
      const selectedReturnFlight = bookingPackage.returnFlights.find((x) => x.isSelected);

      //   let optionPrice = selectedOption.rooms.reduce(
      //     (total, room) => total + (room.options.find((x) => x.isSelected)?.price ?? 0),
      //     0
      //   );

      //   optionPrice += selectedOption.groups.reduce(
      //     (total, g) => total + calculatePerBookingPrice(g),
      //     0
      //   );

      //   selectedOption.optionUnits.forEach((unit) => {
      //     optionPrice += unit.groups.reduce((total, g) => total + calculatePerUnitPrice(g), 0);
      //   });

      //   selectedOption.optionPax.forEach((pax) => {
      //     optionPrice += pax.groups.reduce((total, g) => total + calculatePerPaxPrice(g), 0);
      //   });

      const hasFlight = selectedOption.includedServiceTypes.some((x) => x === 7);
      productDispatch(setHasFlight(hasFlight));

      const hasTranfer = selectedOption.includedServiceTypes.some((x) => x === 13);
      productDispatch(setHasTransfer(hasTranfer));

      qsmDispatch(setBookingRooms(selectedOption.rooms));
      let flightPrice = 0;
      if (selectedOutwardFlight && selectedReturnFlight) {
        qsmDispatch(setFlight({ outward: selectedOutwardFlight, return: selectedReturnFlight }));
        flightPrice = selectedOutwardFlight.price + selectedReturnFlight.price;
      } else {
        qsmDispatch(setFlight(undefined));
      }

      productDispatch(setEstimatedPrice(undefined));
      updatePrice();

      setBookable(true);
    } else {
      productDispatch(setIsUnavailable(true));
      productDispatch(setPrice(undefined));
      productDispatch(setHasFlight(false));
      productDispatch(setHasTransfer(false));
      productDispatch(
        setEstimatedPrice({
          defaultPrice: qsmState?.product?.content?.general?.estimatedPrice ?? undefined,
          promoPrice: qsmState?.product?.content?.general?.estimatedPromoPrice ?? undefined,
        })
      );

      qsmDispatch(setBookingRooms([]));
      qsmDispatch(setFlight(undefined));

      setBookable(false);
    }
  }, [productDispatch, qsmDispatch, qsmState?.bookingPackage, tagIds]);

  const firstRun = useRef(true);
  const loopCount = useRef(0);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }

    // Attempt to prevent infinite loop.
    // We noticed some cases where the same details-call was made multiple times short after each other. We could not find the root cause, but this should limit the damage.
    if (loopCount.current < 10) {
      loopCount.current += 1;
    } else {
      location.reload();
      return;
    }

    setRequestRooms(qsmState?.rooms ?? []);

    const controller = new AbortController();
    const { signal } = controller;

    (async () => {
      fetchPackage(signal);
    })();
    return () => {
      controller.abort();
    };
  }, [
    qsmState?.product,
    qsmState?.dateRange?.fromDate?.valueOf(),
    qsmState?.dateRange?.toDate?.valueOf(),
    qsmState?.loaded,

    // Only refetch when roomDistribution changes, not when acco or regime changes
    JSON.stringify(
      {
        ...qsmState,
        rooms: qsmState?.rooms.map((room) => {
          return {
            ...room,
            accommodationCode: undefined,
            regimeCode: undefined,
          };
        }),
      }?.rooms?.map(sortProps)
    ),
  ]);

  useEffect(() => {
    setRequestRooms(qsmState?.rooms ?? []);
    if (flyInHasBeenOpened) {
      updateHistory(false);
    } else {
      updateHistory(true);
    }
  }, [qsmState?.rooms, qsmState?.flight]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (!isEmpty((window as any).Tawk_API)) {
        if (productState?.regimeFlyInIsOpen || productState?.flightsFlyInIsOpen) {
          (window as any).Tawk_API?.hideWidget();
        } else {
          (window as any).Tawk_API?.showWidget();
        }
      }
    }
  }, [productState?.regimeFlyInIsOpen, productState?.flightsFlyInIsOpen]);

  useEffect(() => {
    if (memberState?.member && factsheetUrl) {
      setShowFactsheet(true);
    } else {
      setShowFactsheet(false);
    }
  }, [memberState, factsheetUrl]);

  return (
    <div className="product-intro">
      <Helmet
        bodyAttributes={{
          class: `${
            productState?.regimeFlyInIsOpen || productState?.flightsFlyInIsOpen
              ? "fly-in--active"
              : ""
          }`,
        }}
      />
      <div className="product-intro__content">
        <div className="product-intro__heading">
          <div className="destination-label">
            <Icon name="ui-marker" />
            {destination}
          </div>
          <h2>{introductionTitle ?? <>&nbsp;</>}</h2>
        </div>
        <div
          className="product-intro__copy"
          dangerouslySetInnerHTML={{ __html: introduction ?? "" }}
        ></div>
        {showFactsheet && (
          <div className="product-intro__factsheet">
            <Link
              target="_blank"
              rel="noopener"
              href={factsheetUrl}
              title={t("DOWNLOAD_FACTSHEET")}
              className="link"
            >
              <Icon name="download" />
              {t("DOWNLOAD_FACTSHEET")}
            </Link>
          </div>
        )}
        <div className="product-intro__media">{mediaComponent}</div>
      </div>
      <div className="product-intro__summary" id="booking">
        <div className="booking-summary">
          <div className="booking-summary__header">
            <div className="booking-summary__hotel">
              {rating && <Rating rating={rating} />}
              <div className="booking-summary__title">{title}</div>
            </div>
            <div className="booking-summary__price">
              {pricingComponent}
              {!qsmState?.isLoading && (
                <div className="package-icons">
                  {productState?.price && (
                    <div className="package-icons__icon">
                      <Icon name="ui-bed" title={t("STAY_INCLUDED")} />
                    </div>
                  )}
                  {productState?.hasFlight && (
                    <div className="package-icons__icon">
                      <Icon name="ui-flight" title={t("FLIGHT_INCLUSIVE")} />
                    </div>
                  )}
                  {productState?.hasTranfer && (
                    <div className="package-icons__icon">
                      <Icon name="ui-transfer" title={t("TRANSFER_INCLUDED")} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="booking-summary__body">
            <div className="booking-summary__item">
              <div className="booking-summary__item-title">
                <Icon name="qsm-user" />
                {t("WHO_YOU_TRAVELING_WITH")}
              </div>
              <ProductIntroRooms></ProductIntroRooms>
              <a href="" onClick={handleRegimeClick}>
                {t("EDIT_ROOM")}
              </a>
            </div>
            <div className="booking-summary__item">
              <div className="booking-summary__item-title">
                <Icon name="qsm-calendar" />
                {t("TRAVEL_PERIOD")}
              </div>
              {qsmState && qsmState.loaded && (
                <DatePicker
                  isProductIntro={true}
                  value={qsmState.dateRange}
                  panelActive={datePanelActive}
                  duration={qsmState.duration}
                  onPanelToggle={(isActive) => {
                    if (!qsmState?.isLoading) {
                      if (isActive) {
                      }
                      setDatePanelActive(isActive);
                    }
                  }}
                  onChange={(value) => {
                    setDateRange(value);
                    if (qsmDispatch) qsmDispatch(setDateRange(value));
                    window.scrollTo(0, 0);
                  }}
                />
              )}
            </div>
            {qsmState?.flight?.outward && qsmState?.flight?.return && (
              <div className="booking-summary__item">
                <div className="booking-summary__item-title">
                  <Icon name="ui-flight" />
                  {t("FLIGHTS")}
                </div>
                <div className="booking-summary__flights">
                  <div className="booking-summary__flights-outward">
                    <div className="booking-summary__flights-label">{t("OUTWARD_FLIGHT")}</div>
                    <div className="booking-summary__flights-info">
                      <div className="booking-summary__flights-info--flight">
                        {qsmState.flight.outward.code} -&nbsp;
                        {
                          first(qsmState.flight.outward.flightMetaData.flightLines)?.travelClass
                        }{" "}
                        -&nbsp;
                        {qsmState.flight.outward.flightMetaData.flightLines.length > 1
                          ? qsmState.flight.outward.flightMetaData.flightLines.length -
                            1 +
                            " " +
                            t("STOPOVERS")
                          : t("DIRECT")}
                      </div>
                      <div className="booking-summary__flights-info--datetime">
                        {format(
                          parse(
                            qsmState.flight.outward.startDateTime,
                            "yyyy-MM-dd'T'HH:mm:ss'Z'",
                            new Date()
                          ),
                          "dd/MM/yyyy"
                        )}
                        &nbsp;
                        {first(qsmState.flight.outward.flightMetaData.flightLines)?.departureTime}
                        &nbsp;-&nbsp;
                        {format(
                          parse(
                            qsmState.flight.outward.endDateTime,
                            "yyyy-MM-dd'T'HH:mm:ss'Z'",
                            new Date()
                          ),
                          "dd/MM/yyyy"
                        )}
                        &nbsp;
                        {last(qsmState.flight.outward.flightMetaData.flightLines)?.arrivalTime}
                        {!isSameDay(
                          new Date(
                            first(qsmState.flight.outward.flightMetaData.flightLines)!.departureDate
                          ),
                          new Date(
                            last(qsmState.flight.outward.flightMetaData.flightLines)!.arrivalDate
                          )
                        ) && (
                          <i className="fas fa-exclamation-circle">
                            <div className="tooltip tooltip--different-day">
                              <span className="tooltip__title">{t("IS_OVERNIGHT_FLIGHT")}</span>
                            </div>
                          </i>
                        )}
                      </div>
                      <div className="booking-summary__flights-airport">
                        {
                          first(qsmState.flight.outward.flightMetaData.flightLines)
                            ?.departureAirportDescription
                        }
                        &nbsp;{">"}&nbsp;
                        {
                          last(qsmState.flight.outward.flightMetaData.flightLines)
                            ?.arrivalAirportDescription
                        }
                      </div>
                    </div>
                  </div>
                  <div className="booking-summary__flights-retour">
                    <div className="booking-summary__flights-label">{t("RETURN_FLIGHT")}</div>
                    <div className="booking-summary__flights-info">
                      <div className="booking-summary__flights-info--flight">
                        {qsmState.flight.return.code} -&nbsp;
                        {first(qsmState.flight.return.flightMetaData.flightLines)?.travelClass}{" "}
                        -&nbsp;
                        {qsmState.flight.return.flightMetaData.flightLines.length > 1
                          ? qsmState.flight.return.flightMetaData.flightLines.length -
                            1 +
                            " " +
                            t("STOPOVERS")
                          : t("DIRECT")}
                      </div>
                      <div className="booking-summary__flights-info--datetime">
                        {format(
                          parse(
                            qsmState.flight.return.startDateTime,
                            "yyyy-MM-dd'T'HH:mm:ss'Z'",
                            new Date()
                          ),
                          "dd/MM/yyyy"
                        )}
                        &nbsp;
                        {first(qsmState.flight.return.flightMetaData.flightLines)?.departureTime}
                        &nbsp;-&nbsp;
                        {format(
                          parse(
                            qsmState.flight.return.endDateTime,
                            "yyyy-MM-dd'T'HH:mm:ss'Z'",
                            new Date()
                          ),
                          "dd/MM/yyyy"
                        )}
                        &nbsp;
                        {last(qsmState.flight.return.flightMetaData.flightLines)?.arrivalTime}
                        {!isSameDay(
                          new Date(
                            first(qsmState.flight.return.flightMetaData.flightLines)!.departureDate
                          ),
                          new Date(
                            last(qsmState.flight.return.flightMetaData.flightLines)!.arrivalDate
                          )
                        ) && (
                          <i className="fas fa-exclamation-circle">
                            <div className="tooltip tooltip--different-day">
                              <span className="tooltip__title">{t("IS_OVERNIGHT_FLIGHT")}</span>
                            </div>
                          </i>
                        )}
                      </div>
                      <div className="booking-summary__flights-airport">
                        {
                          first(qsmState.flight.return.flightMetaData.flightLines)
                            ?.departureAirportDescription
                        }
                        &nbsp;{">"}&nbsp;
                        {
                          last(qsmState.flight.return.flightMetaData.flightLines)
                            ?.arrivalAirportDescription
                        }
                      </div>
                    </div>
                  </div>
                </div>
                {qsmState.bookingPackage?.outwardFlights &&
                  qsmState.bookingPackage?.outwardFlights.length > 1 && (
                    <a href="" onClick={handleFlightsClick}>
                      {t("PICK_ALTERNATE_FLIGHT")}
                    </a>
                  )}
              </div>
            )}
            {memberState?.member && (
              <div className="booking-summary__item">
                <div className="booking-summary__tag-translations">
                  {qsmState?.bookingPackage?.tags.map((tag) => (
                    <label
                      key={tag.id}
                      htmlFor={`tag-translation-${tag.id}`}
                      className="checkbox__label tag-translation"
                    >
                      <LabeledCheckbox
                        value={tag.id}
                        name={`tag-translation-${tag.id}`}
                        checked={tagIds.includes(tag.id)}
                        onChange={handleTagClick}
                      >
                        <span className="tag-translation__title">{tag.title}</span>
                        <br />
                        <span className="tag-translation__description">{tag.description}</span>
                      </LabeledCheckbox>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="booking-summary__footer">
            {qsmState?.isLoading ? (
              <>
                <Loader loaderText={t("LOADING_PACKAGE")} />
              </>
            ) : (
              <>
                <div className="booking-summary__footer-total">
                  <div className="booking-summary__footer-label">
                    {productState?.price?.defaultPrice && (
                      <>{productState?.price?.defaultPrice && <>{t("TOTAL_PRICE")}</>}</>
                    )}
                  </div>
                  <div className="booking-summary__footer-price">
                    {productState?.price?.defaultPrice && (
                      <>
                        {isFetchingPrice ? (
                          <Loader />
                        ) : (
                          formatter.format(productState?.price?.defaultPrice)
                        )}
                      </>
                    )}
                    {!productState?.price?.defaultPrice &&
                      productState?.estimatedPrice?.promoPrice && (
                        <>
                          {t("STARTING_FROM_SHORT")}{" "}
                          {formatter.format(productState?.estimatedPrice?.promoPrice)}{" "}
                          {t("PER_PERSON_SHORT")}
                        </>
                      )}
                  </div>
                </div>
                <div className="booking-summary__footer-actions">
                  {isBookable ? (
                    <button type="button" className="cta" onClick={handleBookClick}>
                      {t("BOOK_NOW")}
                    </button>
                  ) : (
                    <a href="#offer-form" className="cta" onClick={handleAnchorClick}>
                      {t("REQUEST_OFFER")}
                    </a>
                  )}
                  {isBookable && memberState?.member && (
                    <div className="form__group">
                      <button
                        type="button"
                        className="spinner-button cta"
                        onClick={handlePrintOfferClick}
                        disabled={printButtonState.isLoading}
                      >
                        {printButtonState.isLoading ? <Loader></Loader> : ""}
                        {printButtonState.label}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductIntro;
